import React, { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Search, LucideIcon, icons as lucideIconsMap } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { ICON_MAP } from "@/components/ui/LucideIconPicker";

// Type for Lucide icons module
type LucideIconsModule = Record<string, LucideIcon | unknown>;

// Common AAC icons organized by category
const iconCategories = {
  "תקשורת": ["MessageCircle", "MessageSquare", "MessagesSquare", "Speech", "Quote", "Megaphone"],
  "רגשות": ["Heart", "Smile", "Frown", "Meh", "Angry", "ThumbsUp", "ThumbsDown", "Star"],
  "פעולות": ["Play", "Pause", "Stop", "Hand", "HandMetal", "Pointer", "Move", "RotateCw"],
  "אנשים": ["User", "Users", "Baby", "PersonStanding", "Accessibility", "HeartHandshake"],
  "בית": ["Home", "Bed", "Bath", "Lamp", "Sofa", "DoorOpen", "DoorClosed", "Key"],
  "אוכל": ["Apple", "Banana", "Cherry", "Cookie", "Coffee", "CupSoda", "Utensils", "Pizza"],
  "בריאות": ["Stethoscope", "Pill", "Thermometer", "Bandage", "Activity", "HeartPulse"],
  "תחבורה": ["Car", "Bus", "Train", "Plane", "Bike", "Ship", "Truck", "Ambulance"],
  "טבע": ["Sun", "Moon", "Cloud", "CloudRain", "Snowflake", "Wind", "Trees", "Flower"],
  "ספורט": ["Dumbbell", "Trophy", "Medal", "Target", "Bike", "Footprints"],
  "מוזיקה": ["Music", "Music2", "Headphones", "Mic", "Radio", "Volume2"],
  "חינוך": ["Book", "BookOpen", "GraduationCap", "Pencil", "School", "Calculator"],
  "שעון": ["Clock", "Timer", "Calendar", "CalendarDays", "Hourglass", "Watch"],
  "כללי": ["Check", "X", "Plus", "Minus", "HelpCircle", "AlertCircle", "Info", "Zap"],
};

// Flatten all icon names for search
const allIconNames = Object.values(iconCategories).flat();

interface IconPickerProps {
  selectedIcon?: string;
  onSelect: (iconName: string) => void;
}

export function IconPicker({ selectedIcon, onSelect }: IconPickerProps) {
  const [search, setSearch] = useState("");

  const filteredIcons = useMemo(() => {
    if (!search.trim()) {
      return iconCategories;
    }
    
    const searchLower = search.toLowerCase();
    const filtered: Record<string, string[]> = {};
    
    Object.entries(iconCategories).forEach(([category, icons]) => {
      const matchingIcons = icons.filter(icon => 
        icon.toLowerCase().includes(searchLower) ||
        category.toLowerCase().includes(searchLower)
      );
      if (matchingIcons.length > 0) {
        filtered[category] = matchingIcons;
      }
    });
    
    // Also search in all Lucide icons if nothing found in common ones
    if (Object.keys(filtered).length === 0) {
      const lucideMatches = Object.keys(LucideIcons)
        .filter(name => {
          const maybeIcon = (LucideIcons as LucideIconsModule)[name];
          const isRenderableIcon =
            typeof maybeIcon === "function" ||
            (typeof maybeIcon === "object" && maybeIcon !== null);

          return (
            isRenderableIcon &&
            name.toLowerCase().includes(searchLower) &&
            !name.startsWith("create") &&
            name !== "default"
          );
        })
        .slice(0, 30);
      
      if (lucideMatches.length > 0) {
        filtered["תוצאות חיפוש"] = lucideMatches;
      }
    }
    
    return filtered;
  }, [search]);

  const renderIcon = (iconName: string) => {
    const Icon = (LucideIcons as LucideIconsModule)[iconName] as LucideIcon | undefined;
    if (!Icon) return null;
    
    return (
      <button
        key={iconName}
        onClick={() => onSelect(iconName)}
        className={cn(
          "w-12 h-12 flex items-center justify-center rounded-xl border-2 transition-all",
          "hover:border-primary hover:bg-primary/10 hover:scale-105",
          selectedIcon === iconName 
            ? "border-primary bg-primary/20 scale-105" 
            : "border-border bg-card"
        )}
        title={iconName}
      >
        <Icon className="w-6 h-6" />
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חפש אייקון..."
          className="pr-10 text-right"
        />
      </div>
      
      <ScrollArea className="h-[300px]">
        <div className="space-y-4 pr-4">
          {Object.entries(filteredIcons).map(([category, icons]) => (
            <div key={category}>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">{category}</h4>
              <div className="flex flex-wrap gap-2">
                {icons.map(renderIcon)}
              </div>
            </div>
          ))}
          
          {Object.keys(filteredIcons).length === 0 && (
            <p className="text-center text-muted-foreground py-8">לא נמצאו אייקונים</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// Helper to render an icon by name
export function renderIconByName(iconName: string, className?: string) {
  // Try ICON_MAP first (static imports, always reliable),
  // then lucide-react's icons map, then fall back to dynamic lookup
  const Icon = ICON_MAP[iconName]
    || lucideIconsMap[iconName as keyof typeof lucideIconsMap]
    || (LucideIcons as LucideIconsModule)[iconName] as LucideIcon | undefined;
  if (!Icon) return null;
  return <Icon className={className} />;
}
